import React from 'react';
import {
  DndContext,
  KeyboardSensor,
  PointerSensor,
  closestCenter,
  useSensor,
  useSensors,
} from '@dnd-kit/core';
import {
  SortableContext,
  arrayMove,
  rectSortingStrategy,
  sortableKeyboardCoordinates,
  useSortable,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import {
  Box,
  Card,
  CardMedia,
  CircularProgress,
  Grid,
  IconButton,
  Tooltip,
} from '@mui/material';
import {
  Delete as DeleteIcon,
  DragIndicator as DragIndicatorIcon,
  KeyboardArrowLeft as KeyboardArrowLeftIcon,
  KeyboardArrowRight as KeyboardArrowRightIcon,
} from '@mui/icons-material';
import { useTranslation } from 'react-i18next';

const overlayButtonSx = {
  backgroundColor: 'rgba(0, 0, 0, 0.6)',
  color: '#fff',
  width: { xs: 40, sm: 36 },
  height: { xs: 40, sm: 36 },
  '&:hover': { backgroundColor: 'rgba(0, 0, 0, 0.8)' },
};

const StoreGalleryImageCard = ({
  image,
  dragHandleProps,
  isDragging,
  canReorder,
  canDelete,
  deleting,
  disabled,
  isFirst,
  isLast,
  onPreview,
  onDelete,
  onMoveEarlier,
  onMoveLater,
}) => {
  const { t } = useTranslation();

  return (
    <Card
      sx={{
        position: 'relative',
        borderRadius: 2,
        overflow: 'hidden',
        opacity: isDragging ? 0.85 : 1,
        boxShadow: isDragging ? '0 8px 24px rgba(0,0,0,0.18)' : undefined,
      }}
    >
      <CardMedia
        component="img"
        image={image.url}
        alt=""
        onClick={() => onPreview(image.url)}
        sx={{ height: 200, objectFit: 'cover', cursor: 'pointer' }}
      />
      {canReorder && (
        <Box
          {...dragHandleProps}
          sx={{
            position: 'absolute',
            top: 8,
            left: 8,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            width: { xs: 40, sm: 36 },
            height: { xs: 40, sm: 36 },
            borderRadius: '50%',
            backgroundColor: 'rgba(0, 0, 0, 0.6)',
            color: '#fff',
            cursor: 'grab',
            touchAction: 'none',
          }}
          aria-label={t('stores.images.drag')}
        >
          <DragIndicatorIcon fontSize="small" />
        </Box>
      )}
      {canReorder && (
        <Box
          sx={{
            position: 'absolute',
            bottom: 8,
            left: 8,
            display: 'flex',
            gap: 0.5,
          }}
        >
          <Tooltip title={t('stores.images.moveEarlier')}>
            <span>
              <IconButton
                size="small"
                onClick={onMoveEarlier}
                disabled={disabled || isFirst}
                sx={overlayButtonSx}
              >
                <KeyboardArrowLeftIcon fontSize="small" />
              </IconButton>
            </span>
          </Tooltip>
          <Tooltip title={t('stores.images.moveLater')}>
            <span>
              <IconButton
                size="small"
                onClick={onMoveLater}
                disabled={disabled || isLast}
                sx={overlayButtonSx}
              >
                <KeyboardArrowRightIcon fontSize="small" />
              </IconButton>
            </span>
          </Tooltip>
        </Box>
      )}
      {canDelete && (
        <IconButton
          onClick={() => onDelete(image)}
          disabled={deleting}
          sx={{
            position: 'absolute',
            top: 8,
            right: 8,
            ...overlayButtonSx,
          }}
        >
          {deleting ? (
            <CircularProgress size={20} sx={{ color: '#fff' }} />
          ) : (
            <DeleteIcon />
          )}
        </IconButton>
      )}
    </Card>
  );
};

const SortableGalleryItem = ({
  image,
  canReorder,
  canDelete,
  deleting,
  disabled,
  isFirst,
  isLast,
  onPreview,
  onDelete,
  onMoveEarlier,
  onMoveLater,
}) => {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id: image.id, disabled: !canReorder || disabled });

  return (
    <Grid item xs={12} sm={6} md={4}>
      <Box
        ref={setNodeRef}
        sx={{
          transform: CSS.Transform.toString(transform),
          transition,
          zIndex: isDragging ? 2 : 1,
        }}
      >
        <StoreGalleryImageCard
          image={image}
          dragHandleProps={canReorder ? { ...attributes, ...listeners } : undefined}
          isDragging={isDragging}
          canReorder={canReorder}
          canDelete={canDelete}
          deleting={deleting}
          disabled={disabled}
          isFirst={isFirst}
          isLast={isLast}
          onPreview={onPreview}
          onDelete={onDelete}
          onMoveEarlier={onMoveEarlier}
          onMoveLater={onMoveLater}
        />
      </Box>
    </Grid>
  );
};

const SortableStoreGalleryGrid = ({
  images,
  canReorder,
  canDelete,
  deletingIds,
  disabled,
  onReorder,
  onPreview,
  onDelete,
}) => {
  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 8 } }),
    useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates })
  );

  const handleDragEnd = (event) => {
    if (disabled) {
      return;
    }
    const { active, over } = event;
    if (!over || active.id === over.id) {
      return;
    }

    const oldIndex = images.findIndex((image) => image.id === active.id);
    const newIndex = images.findIndex((image) => image.id === over.id);
    if (oldIndex < 0 || newIndex < 0) {
      return;
    }

    onReorder(arrayMove(images, oldIndex, newIndex));
  };

  const handleMove = (index, direction) => {
    if (disabled) {
      return;
    }
    const targetIndex = direction === 'earlier' ? index - 1 : index + 1;
    if (targetIndex < 0 || targetIndex >= images.length) {
      return;
    }

    onReorder(arrayMove(images, index, targetIndex));
  };

  const cards = images.map((image, index) => (
    <SortableGalleryItem
      key={image.id}
      image={image}
      canReorder={canReorder}
      canDelete={canDelete}
      deleting={deletingIds.has(image.id)}
      disabled={disabled}
      isFirst={index === 0}
      isLast={index === images.length - 1}
      onPreview={onPreview}
      onDelete={onDelete}
      onMoveEarlier={() => handleMove(index, 'earlier')}
      onMoveLater={() => handleMove(index, 'later')}
    />
  ));

  return (
    <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={handleDragEnd}>
      <SortableContext items={images.map((image) => image.id)} strategy={rectSortingStrategy}>
        <Grid container spacing={2}>
          {cards}
        </Grid>
      </SortableContext>
    </DndContext>
  );
};

export default SortableStoreGalleryGrid;
